import React from "react"

import Layout from "@/components/Layout"
import SEO from "@/components/SEO"
import WistiaPlayer from "@/components/WistiaPlayer"
import Accordion from "@/components/Accordion"
import { Link } from "gatsby"
import { routes } from "@/utils/routes"
import { StaticImage } from "gatsby-plugin-image"

type PageProps = {
  data: any
}

const SouthWingDevelopment: React.FC<PageProps> = ({ data }: { data: any }) => {
  return (
    <Layout
      sectionOne={
        <>
          <div className="relative h-[67vh] md:min-h-[75vh]">
            <StaticImage
              src="../../static/images/south wing faq pic.png"
              className="absolute inset-0 w-full h-full object-cover -z-50"
              alt="The South Wing"
            />
            <div className="absolute inset-0 bg-[#131312]/50 -z-40"></div>

            <div className="container text-white h-[67vh] md:min-h-[75vh] flex items-center px-4">
              <div className="pl-2 md:pl-6 lg:pl-10 max-w-[1000px] translate-y-10 md:translate-y-16 lg:translate-y-20">
                <div className="pb-4">
                  <h1
                    className="text-left text-white text-[300%] sm:text-[400%] md:text-[531.25%] leading-[1] font-bold normal-case tracking-normal font-gilroy"
                  >
                    The South Wing
                  </h1>
                  <div className="mt-5 h-[8px] sm:h-[10px] w-[200px] sm:w-[320px] bg-primary"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Gold four-option bar below the fold */}
          <div className="bg-primary w-full h-[33vh] md:h-auto md:block">
            <div className="flex flex-col h-full w-full md:grid md:grid-cols-4 md:gap-0 md:items-stretch md:min-h-[84px]">
              <a href={routes.socialMobility()} className="flex-1 flex items-center justify-center px-4 text-center uppercase font-gilroy font-bold text-[#131312] border-b border-white text-[19px] leading-[32.3px] md:border-b-0 md:border-r md:px-2">
                Transformative Bursaries
              </a>
              <a href={routes.southWingDevelopment()} className="flex-1 flex items-center justify-center px-4 text-center uppercase font-gilroy font-bold text-[#131312] border-b border-white text-[19px] leading-[32.3px] md:border-b-0 md:border-r md:px-2">
                The South Wing
              </a>
              <a href={routes.pledge()} className="flex-1 flex items-center justify-center px-4 text-center uppercase font-gilroy font-bold text-[#131312] border-b border-white text-[19px] leading-[32.3px] md:border-b-0 md:border-r md:px-2">
                Sponsor a Paviour
              </a>
              <a href={routes.donate()} className="flex-1 flex items-center justify-center px-4 text-center uppercase font-gilroy font-bold text-[#131312] text-[19px] leading-[32.3px] md:px-2">
                Donate
              </a>
            </div>
          </div>
        </>
      }
    >
      <SEO title="The South Wing | RBAI Foundation" />

      <div className="pt-8 lg:pt-16">
        <div className="container px-4 md:px-4 lg:px-6 xl:px-0">
          <div>
            <h2 className="text-[24px] sm:text-[28px] md:text-[32px] font-gilroy font-bold leading-[0.9]">
              The construction of this new <span className="text-primary">world-class facility</span> will secure the future of RBAI as a <span className="text-primary">centre of academic excellence</span> that supports the educational needs, growth and development of 1,100 pupils and staff.
            </h2>
          </div>
          <div className="grid gap-8 mt-16">
            <WistiaPlayer
              videoStyle={{
                width: "100%",
                height: "100%",
              }}
              videoID={"55u9nh9o9c"}
              className="aspect-video"
            />

            <div className="md:mt-8 mb-8 flex justify-end">
              <Link
                to={routes.donate()}
                className="inline-block bg-primary text-dark px-8 py-4 rounded-sm font-gilroy font-bold text-xl hover:bg-[#d9a01d] transition-colors text-center"
              >
                DONATE NOW
              </Link>
            </div>
            {/* <Link to={getVideoLink(0)}>
              <Challenge
                image={"/images/2023/03/videos/video-1-holding.jpg"}
                title={"Video Title"}
                description={"Video description goes here..."}
                href={null}
                wrapperClass="mb-3"
              />
            </Link> */}
          </div>
        </div>
      </div>

      <div className="container pt-14 px-4 md:px-4 lg:px-6 xl:px-0">
        <div className="flex justify-center">
          <div className="md:text-xl">
            <h3 className="mb-2 md:text-xl font-gilroy font-bold text-primary">Buildings Designed For A Different Era</h3>
            <p className="mb-4 font-gilroy-light text-dark">Teaching and learning styles are constantly evolving, while the use of technology is rapidly advancing. Moreover, a positive learning environment is critical to pupils' mental health and wellbeing. RBAI is restricted by buildings designed for a very different era, as was highlighted by an extensive review of the School's estate.</p>
            <ul className="list-disc ml-4 mb-4">
              <li className="mb-4 font-gilroy-light">Classrooms in the 1920's W Block no longer meet the Department of Education space standards and are not fit for purpose.</li>
              <li className="mb-4 font-gilroy-light">The swimming pool which was built in the 1960s and saw over 12,000 individual usages each year, was closed in 2024 due to unsustainable repair costs.</li>
              <li className="mb-4 font-gilroy-light">The refurbishment of the 1950s dining hall, which serves more than 700 boys daily, has been deemed unviable.</li>
              <li className="mb-4 font-gilroy-light">COVID demonstrated that high quality internal and external spaces are essential for the wellbeing of pupils and staff.</li>
              <li className="mb-4 font-gilroy-light">Creating a new entrance at the rear of the campus will allow RBAI to benefit from significant improvements to the surrounding area, including the new Belfast Grand Central Station and proposed Weavers Cross redevelopment directly behind the School.</li>
            </ul>

            <h3 className="mt-8 mb-2 md:text-xl font-gilroy font-bold text-primary">A Campus For Future Generations</h3>
            <p className="mb-4 font-gilroy-light">Construction of the new South Wing will provide the necessary infrastructure to support the educational needs of 1,100 pupils and staff, securing the School's future as a fully integrated city centre school.</p>
            <p className="mb-4 font-gilroy-light">Plans include 15 general classrooms, a drama suite, a 25m swimming pool, a multi-purpose dining hall, a boardroom suite and the development of landscaped zones to improve pupil and staff wellbeing.</p>
            <p className="mb-4 font-gilroy-light">Near Zero Carbon development will see energy inefficient buildings replaced with new infrastructure, significantly reducing the School's running costs and carbon footprint.</p>
            <p className="mb-4 font-gilroy-light">Whilst facilitating the redevelopment of the site involves the demolition of some existing school infrastructure, the iconic Soane Building and North Wing will be enhanced by high quality buildings designed to be adaptable and flexible in meeting the needs of evolving curriculum requirements.</p>
            <p className="mb-4 font-gilroy-light">Thousands of RBAI pupils will benefit from this investment in the coming decades and it will play an important part in helping us prepare future leaders and innovators.</p>
          </div>
        </div>
      </div>

      {/* <div className="container py-10">
        <Link to={routes.southWingFAQs()} className="link underline hover:no-underline">
          Read our Frequently Asked Questions
        </Link>
      </div> */}

      <div className="container py-10 px-4 md:px-4 lg:px-6 xl:px-0">
        <div className="grid md:grid-cols-3 gap-8">
          <a href="/docs/RBAI 1.pdf" target="_blank" className="flex items-start justify-start p-4 rounded-md hover:bg-gray-100">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 mr-4 flex-shrink-0">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m.75 12l3 3m0 0l3-3m-3 3v-6m-1.5-9H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
            </svg>

            <div>
              <strong className="font-gilroy">View of Proposed Quad, New South Campus & Rear of Soane Building</strong>
            </div>
          </a>

          <a href="/docs/RBAI 2.pdf" target="_blank" className="flex items-start justify-start p-4 rounded-md hover:bg-gray-100">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 mr-4 flex-shrink-0">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m.75 12l3 3m0 0l3-3m-3 3v-6m-1.5-9H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
            </svg>

            <div>
              <strong className="font-gilroy">Proposed Floor Plans</strong>
            </div>
          </a>

          <a href="/docs/RBAI 3.pdf" target="_blank" className="flex items-start justify-start p-4 rounded-md hover:bg-gray-100">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 mr-4 flex-shrink-0">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m.75 12l3 3m0 0l3-3m-3 3v-6m-1.5-9H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
            </svg>

            <div>
              <strong className="font-gilroy">Proposed Building Elevations</strong>
            </div>
          </a>

          <a href="/docs/RBAI 4.pdf" target="_blank" className="flex items-start justify-start p-4 rounded-md hover:bg-gray-100">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 mr-4 flex-shrink-0">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m.75 12l3 3m0 0l3-3m-3 3v-6m-1.5-9H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
            </svg>

            <div>
              <strong className="font-gilroy">Proposed South Wing Visuals</strong>
            </div>
          </a>

          <a href="/docs/RBAI 5.pdf" target="_blank" className="flex items-start justify-start p-4 rounded-md hover:bg-gray-100">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 mr-4 flex-shrink-0">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m.75 12l3 3m0 0l3-3m-3 3v-6m-1.5-9H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
            </svg>

            <div>
              <strong className="font-gilroy">Proposed Dining Hall</strong>
            </div>
          </a>

          <Link to={routes.southWingFAQs()} className="flex items-start justify-start p-4 rounded-md hover:bg-gray-100">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 mr-4 flex-shrink-0">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 5.25h.008v.008H12v-.008Z" />
            </svg>

            <div>
              <strong className="font-gilroy">Frequently Asked Questions</strong>
            </div>
          </Link>
        </div>
      </div>

    </Layout>
  )
}

export default SouthWingDevelopment
